import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Dialog, DialogActions, DialogContent, DialogTitle, TableRow, TableCell } from '@mui/material'
import { Cancel } from '@mui/icons-material'
import SimpleTable from 'components/ui/SimpleTable'
import ErrorMessage from 'components/ui/ErrorMessage'
import { clearGpsVersionStatus, selectCourse } from 'store/course/slice'
import { selectLocale } from 'store/locale/slice'
import { localizedDateTime } from 'utils/formatDate'
import Checkbox from '../Checkbox'
import SearchButton from '../SearchButton'
import { createCourseGpsVersion } from 'store/course/thunks'
import { COURSE_DATA_SOURCE } from 'config'
import useUserRole from 'hooks/useUserRole'

interface Props {
  open: boolean
  close: () => void
  course: Course
  gpsVersion: number | null
}

export default function GpsVersionModal({ open, close, course, gpsVersion }: Props): JSX.Element {
  const [gpsData, setGpsData] = useState<CourseGps[]>([])
  const [validationError, setValidationError] = useState<string>('')

  const { gpsVersionStatus } = useSelector(selectCourse)
  const locale = useSelector(selectLocale)
  const dispatch = useDispatch()
  const intl = useIntl()
  const { isGpsEditor } = useUserRole()

  useEffect(() => {
    if (gpsData.length === 0) {
      const gpsPoints = course.gpsPoints || []
      const filtered = gpsPoints.filter((gps: CourseGps) => gps.gpsVersion === gpsVersion)

      const sorted = filtered.sort((a: CourseGps, b: CourseGps) => {
        if (a.locationType && b.locationType && a.hole === b.hole) {
          return b.locationType.localeCompare(a.locationType)
        }
        return a.hole - b.hole
      })
      setGpsData(sorted)
    }
  }, [course, gpsVersion, gpsData])

  useEffect(() => {
    if (gpsVersionStatus === 'succeeded') {
      dispatch(clearGpsVersionStatus())
      close()
    }
  }, [gpsVersionStatus, close, dispatch])

  const columns: TableColumn[] = [
    {
      id: 'id',
      label: intl.formatMessage({ id: 'strings.id' }),
      width: 10,
    },
    {
      id: 'user',
      label: intl.formatMessage({ id: 'strings.userId' }),
      width: 10,
    },
    {
      id: 'hole',
      label: intl.formatMessage({ id: 'strings.hole' }),
      width: 5,
    },
    {
      id: 'lat',
      label: intl.formatMessage({ id: 'strings.lat' }),
      width: 10,
    },
    {
      id: 'lng',
      label: intl.formatMessage({ id: 'strings.lng' }),
      width: 10,
    },
    {
      id: 'updated',
      label: intl.formatMessage({ id: 'strings.updated' }),
      width: 15,
    },
    {
      id: 'locationType',
      label: intl.formatMessage({ id: 'strings.locationType' }),
      width: 10,
    },
    {
      id: 'official',
      label: intl.formatMessage({ id: 'strings.official' }),
      width: 5,
    },
    {
      id: 'version',
      label: intl.formatMessage({ id: 'strings.version' }),
      width: 5,
    },
  ]

  const validateGpsVersion = (data: CourseGpsVersionData[]): CourseGpsDataValidation => {
    const gpsLocations: string[] = []
    const duplicateLocations: string[] = []
    let valid = true
    data.forEach((gps: CourseGpsVersionData) => {
      const location = `${gps.hole}-${gps.locationType}`
      if (gpsLocations.includes(location)) {
        valid = false
        duplicateLocations.push(location)
      }
      gpsLocations.push(location)
    })
    return { valid, duplicateLocations }
  }

  const submitNewGpsVersion = () => {
    setValidationError('')

    const data: CourseGpsVersionData[] = gpsData.map((gps: CourseGps) => {
      const lat = typeof gps.lat === 'string' ? parseFloat(gps.lat) : gps.lat
      const lng = typeof gps.lng === 'string' ? parseFloat(gps.lng) : gps.lng
      const hole = typeof gps.hole === 'string' ? parseInt(gps.hole) : gps.hole
      return {
        hole: hole,
        accuracy: gps.accuracy,
        lat: lat,
        lng: lng,
        locationType: gps.locationType,
        source: COURSE_DATA_SOURCE.GGB_ADMIN,
      }
    })

    const { valid, duplicateLocations } = validateGpsVersion(data)
    if (!valid) {
      const validationFailedMessage = intl.formatMessage({ id: 'strings.gpsVersionValidationFailed' })
      const duplicateHoleLocations = duplicateLocations.join(', ')
      const message = `${validationFailedMessage}: ${duplicateHoleLocations}`
      setValidationError(message)
      return
    }

    const payload: CourseGpsVersionSavePayload = {
      isUserEditor: isGpsEditor,
      courseId: course.id,
      data: { gpsPoints: data },
    }
    dispatch(createCourseGpsVersion(payload))
  }

  const updateValue = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { name, value, type, checked } = event.target
    const newValue = type === 'checkbox' ? checked : value

    const isInvalidHoleNumber = name === 'hole' && (parseInt(value) < 1 || parseInt(value) > 18)
    if (isInvalidHoleNumber) {
      return
    }

    setGpsData(
      [...gpsData].map((gps: CourseGps, i: number) => {
        if (i !== index) {
          return gps
        }
        return {
          ...gps,
          [name]: newValue,
        }
      }),
    )
  }

  return (
    <StyledDialog open={open} onClose={close}>
      <Title id="form-dialog-title">
        {intl.formatMessage({ id: 'strings.gpsData' })}
        <CloseModal onClick={close} />
      </Title>
      <Content>
        <VersionTitle>
          {intl.formatMessage({ id: 'strings.version' })} {gpsVersion}
        </VersionTitle>
        <GpsTable columns={columns}>
          {gpsData.map((gps: CourseGps, i: number) => {
            return (
              <TableRow key={i}>
                <TableCell>
                  <GpsInput name="id" type="number" value={gps.id} disabled readOnly />
                </TableCell>
                <TableCell>
                  <GpsInput name="creatorId" type="number" value={gps.creatorId || undefined} disabled readOnly />
                </TableCell>
                <TableCell>
                  <GpsInput
                    name="hole"
                    type="number"
                    value={gps.hole}
                    onChange={(e) => updateValue(e, i)}
                    min={1}
                    max={18}
                  />
                </TableCell>
                <TableCell>
                  <GpsInput name="lat" type="string" value={gps.lat || undefined} onChange={(e) => updateValue(e, i)} />
                </TableCell>
                <TableCell>
                  <GpsInput name="lng" type="string" value={gps.lng || undefined} onChange={(e) => updateValue(e, i)} />
                </TableCell>
                <TableCell>
                  <GpsInput
                    name="updated"
                    type="string"
                    value={localizedDateTime(new Date(gps.updatedAt.date), locale.language.code)}
                    disabled
                    readOnly
                  />
                </TableCell>
                <TableCell>
                  <GpsInput name="locationType" type="string" value={gps.locationType || undefined} disabled readOnly />
                </TableCell>
                <TableCellCenter>
                  <Checkbox name="isOfficial" checked={gps.isOfficial} disabled />
                </TableCellCenter>
                <TableCell>
                  <GpsInput name="gpsVersion" type="number" value={gps.gpsVersion || undefined} disabled readOnly />
                </TableCell>
              </TableRow>
            )
          })}
        </GpsTable>
      </Content>
      <DialogActions>
        <CreateNewVersion
          status={gpsVersionStatus}
          text={intl.formatMessage({ id: 'strings.createNewVersion' })}
          onClick={submitNewGpsVersion}
        />
        {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
      </DialogActions>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)`
  & .MuiDialog-paper {
    min-width: 1200px;
  }

  & .MuiTextField-root {
    margin: 20px 20px 20px 0;
  }

  & .MuiAlert-root {
    margin-bottom: 20px;
  }
  & .MuiDialogActions-root {
    justify-content: flex-start;
    margin: 0 20px;
  }
`

const CloseModal = styled(Cancel)`
  float: right;
  cursor: pointer;
`

const Title = styled(DialogTitle)`
  background-color: #404040;
  color: ${(props) => props.theme.customPalette.light};
  margin-bottom: 0;
`

const Content = styled(DialogContent)`
  padding: 35px 40px;
`

const VersionTitle = styled.h2`
  color: ${(props) => props.theme.customPalette.darkGray2};
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  margin-bottom: 20px;
`

const GpsTable = styled(SimpleTable)`
  & td,
  & th {
    padding: 5px 10px !important;
  }
`

const GpsInput = styled.input`
  background-color: ${(props) => props.theme.customPalette.lightGray};
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.lightGray2};
  border-radius: 4px;
  height: 40px;
  padding: 0 0 0 10px;
  font-size: 16px;
  line-height: 24px;
  color: #404040;
  width: 100%;
  max-width: 100%;

  &:disabled {
    border-color: ${(props) => props.theme.customPalette.mediumGray};
    color: ${(props) => props.theme.customPalette.lightGray2};
  }
`

const TableCellCenter = styled(TableCell)`
  text-align: center;
`

const CreateNewVersion = styled(SearchButton)``
